<template>
    <b-list-group-item
        button
        :disabled="isDisabled"
        class="equipment-item my-2"
        @click.prevent="click"
    >
        <div class="d-flex justify-content-between">
            <div v-if="isLoading" class="loader d-flex">
                <div class="d-flex flex-row mr-3 align-items-center">
                    <loading-spinner :size="15" />
                </div>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="name" />
            </div>
            <b-form-checkbox v-else :checked="active" :disabled="isDisabled">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="name" />
                <i
                    v-if="option.selectionState === 'REQUIRED'"
                    v-b-tooltip.hover
                    class="ion-ios-information"
                    title="Required by another selection"
                    aria-hidden="true"
                />
                <i
                    v-else-if="option.selectionState === 'EXCLUDED'"
                    v-b-tooltip.hover
                    class="ion-ios-minus"
                    title="Another option has excluded this option"
                    aria-hidden="true"
                />
            </b-form-checkbox>
            <div class="right">
                <div class="price">
                    <span
                        v-if="option.priceState === 'UNKNOWN'"
                        class="dealer-pricing"
                    >
                        See Dealer For Pricing
                    </span>
                    <span v-else-if="option.msrp && !isIncluded">
                        {{ option.msrp | numeral("$0,0") }}
                    </span>
                    <span v-else-if="(option.msrp && isIncluded) || isIncluded">
                        Inc.
                    </span>
                </div>
                <div v-if="!isDisabled" class="icon">
                    <i
                        v-if="extended"
                        :class="
                            showExpanded ? 'ion-chevron-down' : 'ion-chevron-up'
                        "
                        aria-hidden="true"
                        @click.stop="showExpanded = !showExpanded"
                    />
                </div>
            </div>
        </div>
        <b-collapse
            v-if="extended"
            :id="'option-extended-' + option.chromeOptionCode"
            v-model="showExpanded"
            class="mt-2"
        >
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="py-3 px-4 m-0" v-html="extended" />
        </b-collapse>
    </b-list-group-item>
</template>

<script>
import _ from "lodash";
import { get } from "vuex-pathify";
import LoadingSpinner from "Components/LoadingSpinner";

export default {
    components: { LoadingSpinner },
    props: {
        option: {
            type: Object,
            required: true
        },
        groupSize: {
            type: Number,
            required: true
        },
        active: {
            type: Boolean,
            required: false,
            default: false
        }
    },

    data() {
        return {
            showExpanded: false,
            itemLoading: false
        };
    },

    computed: {
        loading: get("configure/loading"),
        checklistItems: get("configure/configuration@checklistItems"),
        isLoading() {
            return this.loading && this.itemLoading;
        },
        name() {
            const primaryName = _.get(
                _.find(_.get(this.option, "descriptions"), {
                    type: "PRIMARY_NAME"
                }),
                "description"
            );
            const consumerFriendly = _.filter(
                _.get(this.option, "descriptions"),
                { type: "CONSUMER_FRIENDLY" }
            );

            if (_.size(consumerFriendly) > 0) {
                return consumerFriendly[0].description;
            }

            return primaryName;
        },
        extended() {
            const extended = _.get(
                _.find(_.get(this.option, "descriptions"), {
                    type: "EXTENDED"
                }),
                "description"
            );
            const consumerFriendly = _.filter(
                _.get(this.option, "descriptions"),
                { type: "CONSUMER_FRIENDLY" }
            );

            if (_.size(consumerFriendly) > 1) {
                return consumerFriendly[1].description;
            }

            return extended;
        },

        isIncluded() {
            return (
                _.indexOf(
                    ["INCLUDED"],
                    _.get(this.option, "selectionState")
                ) !== -1
            );
        },

        isDisabled() {
            if (this.isLoading) {
                return true;
            }

            if (_.get(this.option, "priceState") === "UNKNOWN") {
                return true;
            }

            // If required by checklist
            let isChecklistItem = false;
            _.each(this.checklistItems, checklistItem => {
                const found =
                    _.indexOf(
                        checklistItem.chromeOptionCodes,
                        this.option.chromeOptionCode
                    ) !== -1;
                if (found) {
                    isChecklistItem = true;
                }
            });

            return (
                (isChecklistItem && this.groupSize === 1) ||
                _.indexOf(
                    ["INCLUDED", "REQUIRED", "UPGRADED"],
                    _.get(this.option, "selectionState")
                ) !== -1
            );
        }
    },

    watch: {
        loading(newVal, oldVal) {
            if (!newVal && oldVal) {
                this.itemLoading = false;
            }
        }
    },

    methods: {
        click() {
            if (!this.disabled) {
                this.itemLoading = true;
                this.$emit("select", this.option);
            }
        }
    }
};
</script>

<style lang="scss">
.equipment-item {
    background-color: $very-light-gray;
    box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.1);
    border: none;

    .dealer-pricing {
        font-size: 14px;
    }

    &.highlighted {
        background-color: #fddad3;
        color: #7f2411;
    }

    &:not(.disabled):hover {
        box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.35);
    }

    &:focus {
        outline: none;
        background-color: $very-light-gray;
    }

    &:active {
        background-color: $very-light-gray;
    }

    .right {
        min-width: 75px;
        text-align: right;
        display: flex;
        justify-content: space-between;

        .icon {
            margin-left: 5px;

            i {
                color: $muted-dark-gray;
            }
        }
    }

    .custom-radio {
        width: 100%;
    }

    label {
        font-family: $helvetica-55-Roman;
        width: 100%;
    }

    .price {
        color: $primary-blue;
        font-size: 16px;
        font-weight: normal;
        text-align: right;
        min-width: 50px;
    }

    @include media-breakpoint-up(md) {
        padding: 1.5rem 1.25rem;

        .price {
            font-size: 18px;
        }
    }
}
</style>
